import {
  useFactorLimitChangeMutation,
  useGetFactorsBranchesQuery,
} from '@/app/api';
import { Currency, FactorLimit } from '@/app/api/types';
import { getFetchEror } from '@/app/helpers';
import { BaseModalRef } from '@/app/helpers/types';
import { useOpen } from '@/app/hooks';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { FormDatePicker } from '@/shared/inputs/form-date-picker';
import { FormNumericInput } from '@/shared/inputs/form-numeric-input';
import { FormTextInput } from '@/shared/inputs/form-text-input';
import ModalTemplate from '@/shared/modal/modal-template';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { UIDefinitionList } from '@/shared/ui/ui-definition-list';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import {
  Chip,
  Dialog,
  FormControl,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  data: FactorLimit;
}

interface FormValues {
  amount: string;
  detail: string;
  branchId: number | null;
  generalAgreementDocumentNumber?: string;
  generalAgreementDocumentDate?: Dayjs | null;
  generalAgreementState?: 'exists';
  currency: Currency;
}

export const FactorChangeLimitModal = forwardRef<BaseModalRef, Props>(
  ({ data }, ref) => {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const { open, setOpen } = useOpen();
    const defaultValues: FormValues = {
      amount: (data.availableLimit + data.usedLimit).toFixed(2),
      detail: '',
      branchId: data.branch?.id ?? null,
      generalAgreementDocumentNumber: data.generalAgreement?.number || '',
      generalAgreementDocumentDate: data.generalAgreement?.date
        ? dayjs(data.generalAgreement?.date, 'DD.MM.YYYY')
        : null,
      currency: data.currency as Currency,
    };

    const { reset, control, handleSubmit } = useForm<FormValues>({
      defaultValues,
    });

    const [changeLimit, { isLoading }] = useFactorLimitChangeMutation();

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    const onOpen = () => {
      reset(defaultValues);
      setOpen(true);
    };

    const onClose = () => {
      setOpen(false);
    };

    const onSubmit = async (values: FormValues) => {
      try {
        await changeLimit({
          id: data.unique,
          amount: Number(values.amount.replace(/\s/g, '')),
          branchId: values.branchId,
          detail: values.detail,
          generalAgreementDocumentNumber: values.generalAgreementDocumentNumber,
          generalAgreementDocumentDate: values.generalAgreementDocumentDate
            ? values.generalAgreementDocumentDate.format('DD.MM.YYYY')
            : null,
          generalAgreementState: 'exists',
          currency: values.currency,
        }).unwrap();
        showNotification(t('limit_changed_successfully'), 'success');
        onClose();
      } catch (err) {
        showNotification(getFetchEror(err), 'error');
      }
    };

    const { data: branches, isLoading: isBranchesLoading } =
      useGetFactorsBranchesQuery();

    return (
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            maxWidth: 920,
            width: '100%',
          },
        }}
      >
        <ModalTemplate
          onClose={onClose}
          title={t('limit_control')}
          submitBtnText={t('save')}
          onSubmit={handleSubmit(onSubmit)}
          loading={isLoading}
        >
          <Grid spacing={2} container>
            <Grid item xs={12} md={4}>
              <UIDefinitionList
                term={t('status')}
                description={(() => {
                  switch (data.status) {
                    case 'active':
                      return <Chip label={t('active')} color='success' />;
                    case 'overdue':
                      return <Chip label={t('overdue')} color='error' />;
                    case 'suspended':
                      return <Chip label={t('suspended')} color='info' />;
                    default:
                      break;
                  }
                })()}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <UIDefinitionList
                term={t('client')}
                description={data.company.name}
                descriptionSx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <UIDefinitionList
                term={t('tin')}
                description={data.company.tin}
              />
            </Grid>
          </Grid>

          <Stack spacing={2}>
            <FormControl required>
              <UIFormLabel>{t('limit_sum')}:</UIFormLabel>
              <FormNumericInput
                control={control}
                name='amount'
                required
                thousandSeparator={' '}
                rules={{
                  required: true,
                }}
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel>{t('branch')}:</UIFormLabel>

              <Controller
                control={control}
                name='branchId'
                render={({ field }) => (
                  <UIAutocomplete
                    value={
                      branches?.data.find(
                        (branch) => branch.unique === field.value
                      ) || null
                    }
                    options={branches?.data ?? []}
                    getOptionLabel={(option) => option.branchName}
                    isOptionEqualToValue={(option, value) =>
                      option.unique === value.unique
                    }
                    loading={isBranchesLoading}
                    TextFieldProps={{
                      placeholder: t('common:not_selected'),
                    }}
                    onChange={(_event, value) => {
                      field.onChange(value?.unique ?? null);
                    }}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </FormControl>
            <Typography fontSize={17} fontWeight={600}>
              {t('general_agreement')}
            </Typography>
            <Stack direction='row' spacing={2} maxWidth={'60%'}>
              <FormControl>
                <UIFormLabel>
                  {t('payment_document.document_number')}:
                </UIFormLabel>
                <FormTextInput
                  control={control}
                  name='generalAgreementDocumentNumber'
                  rules={{ required: true }}
                />
              </FormControl>
              <FormControl fullWidth>
                <UIFormLabel>
                  {t('payment_document.document_date')}:
                </UIFormLabel>
                <FormDatePicker
                  control={control}
                  name='generalAgreementDocumentDate'
                  rules={{ required: true }}
                />
              </FormControl>
            </Stack>
            <FormControl required>
              <UIFormLabel>{t('base')}</UIFormLabel>

              <FormTextInput
                control={control}
                name='detail'
                rules={{
                  required: true,
                }}
                multiline
                rows={5}
              />
            </FormControl>
          </Stack>
        </ModalTemplate>
      </Dialog>
    );
  }
);
